class YGImageLazyLoader extends HTMLElement {
  getLazyLoader() {
    if (!this._lazyLoader) {
      this._lazyLoader = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = entry.target;
              const src = img.getAttribute('data-src');

              if (!src) {
                observer.unobserve(img);
                return;
              }

              img.src = src;
              img.removeAttribute('data-src');
              observer.unobserve(img);
            }
          });
        },
        {
          rootMargin: '50px'
        }
      );
    }
    return this._lazyLoader;
  }
}

class YGVideoLazyLoader extends HTMLElement {
  getLazyLoader() {
    if (!this._lazyLoader) {
      this._lazyLoader = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.playVideo();
              observer.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: '50px'
        }
      );
    }
    return this._lazyLoader;
  }
}
class YGUtils extends HTMLElement {
  // 防抖函数 - 延迟执行函数，避免频繁调用
  debounce(func, delay) {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  }

  // 节流函数 - 限制函数执行频率
  throttle(func, limit) {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  get #getCurrentSlidesPerView() {
    if (!this['breakpoints']) return 1;

    const screenWidth = window.innerWidth;

    // 将breakpoints按数值从小到大排序
    const sortedKeys = Object.keys(this['breakpoints'])
      .map(Number)
      .sort((a, b) => a - b);

    let current = sortedKeys[0]; // 默认取最小的

    // 遍历找到小于等于当前屏幕宽度的最大key
    for (let i = 0; i < sortedKeys.length; i++) {
      if (screenWidth >= sortedKeys[i]) {
        current = sortedKeys[i];
      } else {
        break;
      }
    }

    return this['breakpoints'][current].slidesPerView;
  }

  #getAllAttributes(dom) {
    let booleanAttrs = [
      'loop',
      'rewind',
      'pagination',
      'navigation',
      'scrollbar',
      'centered-slides'
    ];
    let jsonAttrs = ['breakpoints'];
    for (const attr of dom.attributes) {
      if (attr.name.startsWith('data-')) {
        const cleanName = attr.name.slice(5); // 去除 'data-' 前缀

        let value = attr.value;
        if (booleanAttrs.includes(cleanName)) {
          value = value === 'true';
        } else if (jsonAttrs.includes(cleanName)) {
          value = this.#parseSwiperBreakpoints(value);
        }

        this[cleanName] = value;
      }
    }
  }

  #parseSwiperBreakpoints(str) {
    const result = {};
    const pairs = str.split(',');

    for (const pair of pairs) {
      const [breakpoint, values] = pair.split(':');
      if (!breakpoint || !values) continue;

      let [slides, space] = values.split('-').map(Number);

      if (this['col-is-auto']) {
        slides = 'auto';
      }
      result[breakpoint.trim()] = {
        slidesPerView: slides,
        spaceBetween: space
      };
    }

    return result;
  }

  /**
   * Swiper 11 native loop is unreliable with centeredSlides + fractional
   * slidesPerView. For that combo we pre-clone slides and silently jump
   * back into the middle band to simulate infinite looping.
   */
  #prepareInfiniteSlides(dom) {
    const root = dom || this;
    const wrapper = root.querySelector('.swiper-wrapper');
    if (!wrapper) {
      return { originalLength: 0, initialSlide: 0, useFakeLoop: false };
    }

    const originalSlides = Array.from(wrapper.children).filter((el) =>
      el.classList.contains('swiper-slide')
    );
    const originalLength = originalSlides.length;
    if (!originalLength) {
      return { originalLength: 0, initialSlide: 0, useFakeLoop: false };
    }

    const wantsLoop = !!this['loop'] && !this['rewind'];
    const useFakeLoop = wantsLoop && !!this['centered-slides'];

    if (!wantsLoop) {
      return { originalLength, initialSlide: 0, useFakeLoop: false };
    }

    const cloneSlide = (slide) => {
      const clonedElement = slide.cloneNode(true);
      clonedElement.classList.remove(
        'swiper-slide-active',
        'swiper-slide-prev',
        'swiper-slide-next',
        'swiper-slide-duplicate',
        'swiper-slide-duplicate-active',
        'swiper-slide-duplicate-prev',
        'swiper-slide-duplicate-next'
      );
      clonedElement.removeAttribute('data-swiper-slide-index');
      return clonedElement;
    };

    if (useFakeLoop) {
      // 5 copies => middle set starts at originalLength * 2
      const copies = 5;
      const targetCount = originalLength * copies;
      while (
        wrapper.querySelectorAll(':scope > .swiper-slide').length < targetCount
      ) {
        originalSlides.forEach((slide) => {
          if (
            wrapper.querySelectorAll(':scope > .swiper-slide').length >=
            targetCount
          ) {
            return;
          }
          wrapper.appendChild(cloneSlide(slide));
        });
      }

      root.dataset.ygOriginalSlides = String(originalLength);
      root.dataset.ygFakeLoop = 'true';
      return {
        originalLength,
        initialSlide: originalLength * Math.floor(copies / 2),
        useFakeLoop: true
      };
    }

    // Native loop: clone before init so Swiper has enough slides.
    let slidesPerView = this.#getCurrentSlidesPerView;
    if (slidesPerView === 'auto' || slidesPerView == null) slidesPerView = 1;
    slidesPerView = Number(slidesPerView) || 1;
    const minSlides = Math.max(
      Math.ceil(slidesPerView) + 1,
      Math.ceil(slidesPerView * 2)
    );

    while (
      wrapper.querySelectorAll(':scope > .swiper-slide').length < minSlides
    ) {
      originalSlides.forEach((slide) => {
        if (
          wrapper.querySelectorAll(':scope > .swiper-slide').length >= minSlides
        ) {
          return;
        }
        wrapper.appendChild(cloneSlide(slide));
      });
    }

    root.dataset.ygOriginalSlides = String(originalLength);
    return { originalLength, initialSlide: 0, useFakeLoop: false };
  }

  #getLogicalIndex(swiper, originalLength) {
    if (!originalLength) return swiper.realIndex || 0;
    return (
      ((swiper.activeIndex % originalLength) + originalLength) % originalLength
    );
  }

  #syncLoopPagination(swiper, originalLength) {
    if (!originalLength || !this['pagination']) return;

    const bullets = this.querySelectorAll('.swiper-pagination-bullet');
    if (!bullets.length) return;

    const logicalIndex = this.#getLogicalIndex(swiper, originalLength);
    bullets.forEach((bullet, index) => {
      bullet.style.display = index >= originalLength ? 'none' : '';
      bullet.classList.toggle(
        'swiper-pagination-bullet-active',
        index === logicalIndex
      );
    });
  }

  #bindFakeLoop(swiper, originalLength, initialSlide) {
    if (!swiper || !originalLength) return;

    const normalize = () => {
      const total = swiper.slides.length;
      const band = originalLength;
      const index = swiper.activeIndex;

      if (index < band) {
        swiper.slideTo(index + band * 2, 0, false);
      } else if (index >= total - band) {
        swiper.slideTo(index - band * 2, 0, false);
      }
    };

    swiper.on('transitionEnd', normalize);
    swiper.on('touchEnd', () => {
      window.setTimeout(normalize, 0);
    });

    // Map visible bullets (0..originalLength-1) to the middle band.
    swiper.on('paginationRender', () => {
      this.#syncLoopPagination(swiper, originalLength);
    });

    const paginationEl = swiper.pagination?.el;
    if (paginationEl) {
      paginationEl.addEventListener(
        'click',
        (event) => {
          const bullet = event.target.closest('.swiper-pagination-bullet');
          if (!bullet) return;

          const bullets = Array.from(
            paginationEl.querySelectorAll('.swiper-pagination-bullet')
          ).filter((item, index) => index < originalLength);
          const bulletIndex = bullets.indexOf(bullet);
          if (bulletIndex < 0) return;

          event.preventDefault();
          event.stopPropagation();
          swiper.slideTo(
            initialSlide - (initialSlide % originalLength) + bulletIndex
          );
        },
        true
      );
    }
  }

  #getBaseSlidesConfig() {
    const breakpoints = this['breakpoints'];
    if (!breakpoints) {
      return { slidesPerView: 1, spaceBetween: 0 };
    }

    const sortedKeys = Object.keys(breakpoints)
      .map(Number)
      .sort((a, b) => a - b);
    const base = breakpoints[sortedKeys[0]] || {};
    return {
      slidesPerView: base.slidesPerView || 1,
      spaceBetween: base.spaceBetween || 0
    };
  }

  initSwiper(dom, thumbnails) {
    this.#getAllAttributes(dom || this);
    const root = dom || this;

    // Custom elements default to inline; Swiper needs a block-level host width.
    root.classList.add('swiper', 'swiper-container');
    if (getComputedStyle(root).display === 'inline') {
      root.style.display = 'block';
    }

    const { originalLength, initialSlide, useFakeLoop } =
      this.#prepareInfiniteSlides(root);
    const SwiperClass = window.Swiper || window.MinimogLibs?.Swiper;
    const useNativeLoop = !!this['loop'] && !this['rewind'] && !useFakeLoop;
    const { slidesPerView, spaceBetween } = this.#getBaseSlidesConfig();
    const config = {
      // 这里放置 swiper 的配置
      effect: this['effect'],
      freeMode: !!this['is-marquee'],
      loop: useNativeLoop,
      rewind: this['rewind'] || false,
      speed: Number(this['speed']) || 300,
      slidesPerView,
      spaceBetween,
      centeredSlides: !!this['centered-slides'],
      initialSlide: useFakeLoop ? initialSlide : 0,
      watchOverflow: true,
      autoplay: this['autoplay']
        ? {
            delay: Number(this['autoplay']),
            stopOnLastSlide: false,
            disableOnInteraction: false,
            waitForTransition: !this['is-marquee'],
            pauseOnMouseEnter: !this['is-marquee']
          }
        : false,
      pagination: this['pagination']
        ? {
            el: this['id'] ? `.${this['id']}-pagination` : '.swiper-pagination',
            type: this['pagination-type'] ? this['pagination-type'] : 'bullets',
            clickable: true
          }
        : false,
      navigation: this['navigation']
        ? {
            nextEl: this['id'] ? `.${this['id']}-next` : '.swiper-button-next',
            prevEl: this['id'] ? `.${this['id']}-prev` : '.swiper-button-prev'
          }
        : false,
      breakpoints: this['breakpoints'] ? this['breakpoints'] : false,
      on: {
        init: (e) => {
          if (useFakeLoop) {
            this.#bindFakeLoop(e, originalLength, initialSlide);
          }
          this.#syncLoopPagination(e, originalLength);
        },
        slideChange: (e) => {
          this.#syncLoopPagination(e, originalLength);
        }
      }
    };

    if (thumbnails) {
      config.thumbs = { swiper: thumbnails };
    }

    const swiper = new SwiperClass(root, config);

    return swiper;
  }

  getLatestDom(requestUrlOrCallback, callback) {
    // 支持两种调用方式：
    // 1. getLatestDom(callback) -> 使用当前页面 URL
    // 2. getLatestDom(requestUrl, callback)
    let requestUrl = requestUrlOrCallback;
    if (typeof requestUrlOrCallback === 'function') {
      callback = requestUrlOrCallback;
      requestUrl = window.location.href;
    } else {
      requestUrl = requestUrl || window.location.href;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    const promise = fetch(requestUrl, {
      signal: this.abortController.signal
    })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        if (typeof callback === 'function') {
          callback(html);
        }
        return html;
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
        // 将错误继续抛出，便于外部处理
        throw error;
      });

    // 返回 Promise，调用者可以选择使用回调或 await 返回值
    return promise;
  }
}
class YGSwiper extends YGUtils {
  constructor() {
    super();
    this.swiper = null;
  }
  connectedCallback() {
    // 避免多次初始化
    if (this.swiper || this._swiperInitPending) return;

    const SwiperClass = window.Swiper || window.MinimogLibs?.Swiper;
    if (!SwiperClass) {
      // Swiper 还没加载好，稍后再试
      this._swiperInitTimer = setTimeout(() => this.connectedCallback(), 50);
      return;
    }

    // Wait a frame so the host has a real layout width before Swiper measures.
    this._swiperInitPending = true;
    requestAnimationFrame(() => {
      this._swiperInitPending = false;
      if (this.swiper || !this.isConnected) return;
      this.swiper = this.initSwiper(this);
      // Recalculate after fonts/images settle.
      requestAnimationFrame(() => {
        try {
          this.swiper?.update?.();
        } catch (e) {}
      });
    });
  }

  disconnectedCallback() {
    if (this._swiperInitTimer) {
      clearTimeout(this._swiperInitTimer);
    }
    this._swiperInitPending = false;
  }
}
if (!customElements.get('yg-swiper')) {
  customElements.define('yg-swiper', YGSwiper);
}

class YGSelect extends YGUtils {
  constructor() {
    super();
  }
  get sectionsEl() {
    if (this._sectionsEl) return this._sectionsEl;

    this._sectionsEl = {
      'current-option': this.querySelector('.yg-current-option'),
      'current-option-label': this.querySelector('.yg-current-option-label'),
      options: this.querySelector('.yg-options'),
      'options-list':
        this.querySelectorAll('.yg-option').length > 0
          ? this.querySelectorAll('.yg-option')
          : Array.from(this.querySelector('.yg-options').children),
      body: document.body
    };

    return this._sectionsEl;
  }
  connectedCallback() {
    this.sectionsEl['current-option']?.addEventListener(
      'click',
      this.clickCurrentOption.bind(this)
    );
    this.sectionsEl['body']?.addEventListener(
      'click',
      this.clickBody.bind(this)
    );
    this.sectionsEl['options-list']?.forEach((option) => {
      option.addEventListener('click', () => this.clickOption(option));
    });
  }
  clickCurrentOption() {
    this.openOptions();
  }
  clickOption(option) {
    this.sectionsEl['current-option-label'].innerText = option.innerText;
    if (option.dataset.value) {
      this.sectionsEl['current-option'].dataset.currentOption =
        option.dataset.value;
    }
  }
  clickBody(event) {
    if (!this.contains(event.target)) {
      this.cloneOptions();
    }
  }
  openOptions() {
    if (this.sectionsEl['options'].classList.contains('active')) {
      this.cloneOptions();
      return;
    }

    this.sectionsEl['options'].classList.add('active');
  }
  cloneOptions() {
    this.sectionsEl['options'].classList.remove('active');
  }
}

if (!window.customElements.get('yg-select')) {
  window.customElements.define('yg-select', YGSelect);
}
